'use client';

import {useEulaAcceptance, useEulaVersionContent} from '@/hooks/eula';
import {Button, Modal, ModalProps, Stack, Text} from '@eds-web/react';
import {useMessages} from '@epic-mw/i18n';
import {JSX, ReactNode, SyntheticEvent, useEffect, useState} from 'react';
import {ModalLoading} from '../loading/ModalLoading';
import {EulaItem} from './Eula.molecule';
import {SafeHTML, Skeleton} from '@/components/atoms';
import {css} from '@panda/css';
import {useDebounce, useQuickLinks} from '@/hooks';
import {getMessage} from '@/lib/utils/i18n.utils';
import {AnalyticEventProps} from '@/types';
import {emitEvent, useEmitEventOnClick} from '@/lib/utils/analytics';
import {useCommonComponentContext} from '@/context/ComponentContext';

const modalStyle = css({
	smDown: {
		display: 'block !important',
		position: 'fixed !important'
	},

	'& > div:first-child': {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		smDown: {
			maxHeight: '100% !important'
		},
		height: {
			base: '100%',
			md: '85%'
		}
	}
});

interface EulaProps extends Omit<ModalProps, 'isOpen' | 'content'> {
	open: boolean;
	eulaId: string;
	version: number;
	locale: string;
	onClose?: (e?) => void;
	onAccept?: (e?) => void;
	onAcceptSuccessful?: (e?) => void;
	disclaimer?: {
		title: ReactNode;
		body: ReactNode;
	};
	analytics?: AnalyticEventProps;
}

export const EulaModal = ({
	open,
	eulaId,
	version,
	locale: userLocale,
	onClose,
	onAccept,
	onAcceptSuccessful,
	analytics,
	...modalProps
}: EulaProps) => {
	const {isRTL} = useCommonComponentContext();
	const dir = isRTL ? 'rtl' : 'ltr';
	const messages = useMessages();
	const quickLinks = useQuickLinks(['faqAccountBalance', 'help']);
	const [submit, setSubmit] = useState(false);
	const eula = useEulaVersionContent(eulaId, version, userLocale);
	const {key, title, body, locale} = eula.eulaContent! || {};
	const acceptance = useEulaAcceptance(eulaId, locale, submit, version);
	const accepted = acceptance.accept?.accepted;
	const error = eula.error || acceptance.error;
	//eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const errorMessage = typeof error === 'string' ? error : ((error as any)?.error as string);
	const isLoading = !eula.eulaContent || eula.isLoading || acceptance.isLoading;

	const emitClickEvent = useEmitEventOnClick({
		...analytics!,
		eventValue: {
			...analytics?.eventValue,
			locale: userLocale,
			eulaId,
			eulaVersion: String(version),
			eulaLocale: locale
		}
	});

	const handleSubmit = useDebounce(<T extends HTMLElement>(e: SyntheticEvent<T>) => {
		emitClickEvent(e, {status: 'initiate', accept: 'true'});
		onAccept?.(e);
		setSubmit(true);
	});

	const handleClose = <T extends HTMLElement>(e?: SyntheticEvent<T>) => {
		emitClickEvent(e, {status: 'error', error: 'cancelled', accept: 'false'});
		onClose?.(e);
	};

	// fixed issue with infinite render, but i ought to relook here again because
	// tracking event for accepting eula in this page sends multiple times
	useEffect(() => {
		if (!error && submit && accepted) {
			emitClickEvent(null, {status: 'success', accept: 'true'});
			onAcceptSuccessful?.();
		}
	}, [onAcceptSuccessful, submit, accepted, error]);

	useEffect(() => {
		if (!open) return;
		emitEvent(
			{
				eventCategory: 'pageview',
				interactionType: 'pageview',
				eventLabel: 'PageView_V2',
				eventValue: {
					subView: 'Accept Terms',
					locale: userLocale,
					eulaId,
					eulaVersion: String(version),
					eulaLocale: locale
				}
			},
			'PageView'
		);
	}, [open]);

	/** render eula based on eula locale,
	 * which could fallback as english whilst user is on another locale */
	const renderEulaDir = (jsx: ReactNode | JSX.Element) => {
		// eula locale
		if (locale === 'ar') {
			return <bdo dir="rtl">{jsx}</bdo>;
		}
		return jsx;
	};

	return (
		<Modal
			className={css({
				position: 'relative',
				width: '100% !important',
				maxWidth: '1024px !important',
				margin: 'auto',
				height: '100% !important',
				smDown: {
					maxHeight: '100% !important',
					borderRadius: '0 !important'
				},
				'& > div:first-child': {
					zIndex: 11
				},
				'& > div:nth-child(2)': {
					height: '100%'
				},
				'& > div:last-child': {
					marginTop: 'auto',
					height: 'fit-content',
					zIndex: 10
				}
			})}
			containerProps={{className: modalStyle, style: {zIndex: 9999}}}
			size="md"
			isOpen={open}
			onOpenChange={(isOpen) => {
				// seems like this is never called when `isOpen === true` :think:
				if (!isOpen) handleClose();
			}}
			fullWidthButtons
			title={
				errorMessage ? <></> : renderEulaDir(<p className={css({margin: 0})}>{title}</p>)
			}
			closeLabel={messages['epic.app.close'] || 'Close'}
			aria-labelledby={messages['epic.app.terms.disclaimer.title'] || 'EULA Agreement'}
			{...modalProps}
			content={
				<>
					{isLoading && !errorMessage && <ModalLoading />}
					{errorMessage ? (
						<>
							<Skeleton
								className={css({
									position: 'absolute',
									left: 0,
									zIndex: 10,
									height: '100%'
								})}
							/>
							<p
								className={css({
									margin: '16px',
									zIndex: 10,
									position: 'relative',
									textAlign: 'center'
								})}>
								{messages[errorMessage] || errorMessage}
							</p>
						</>
					) : (
						<Stack gap={24} className={css({'&>*': {width: '100%'}})}>
							<bdo dir={dir}>
								<Stack gap={24}>
									<Text variant="headingMD">
										{messages['epic.app.terms.disclaimer.title']}
									</Text>
									<Text variant="paragraphMD" color="secondary">
										<SafeHTML
											dangerouslySetInnerHTML={{
												__html: messages['epic.app.terms.disclaimer.desc.1']
											}}
										/>
									</Text>
									<Text
										variant="paragraphMD"
										color="secondary"
										className="nested-link">
										<SafeHTML
											dangerouslySetInnerHTML={{
												__html: getMessage(
													messages,
													'epic.app.terms.disclaimer.desc.2',
													[quickLinks.faqAccountBalance, quickLinks.help]
												) as string
											}}
										/>
									</Text>
									<Text variant="headingMD">
										{messages['epic.app.terms.title']}
									</Text>
								</Stack>
							</bdo>
							{renderEulaDir(
								<EulaItem
									locale={locale}
									eulaId={key}
									body={body}
									// lastModifiedTimestamp={lastModifiedTimestamp}
									// lastModifiedTimestampProps={{ style: { justifyContent: isRTL ? "right" : "left" } }}
								/>
							)}
						</Stack>
					)}
				</>
			}
			submitButton={
				<Button
					disabled={isLoading}
					size="xl"
					onClick={handleSubmit}
					className={css({display: errorMessage ? 'none !important' : 'inherit'})}>
					{messages['epic.app.terms.accept']}
				</Button>
			}
			closeButton={
				<Button variant="solid" size="xl" onClick={handleClose}>
					{messages[errorMessage ? 'epic.app.close' : 'epic.app.terms.decline']}
				</Button>
			}
		/>
	);
};
